import { Controller } from '@hotwired/stimulus'
import { getItem, setItem } from '../utils/local-storage'
import { showPreloader } from '../utils/preloader'
import {
  fetchPlaceLocation,
  fetchPlaceSuggestions,
  locationKey,
  placesAvailable
} from '../utils/places'

const SUGGESTIONS_DEBOUNCE = 250

// Точка пользователя нужна только чтобы подвинуть порядок подсказок Google.
// Дольше 15 минут держать её незачем, а спрашивать браузер на каждый фокус —
// тем более.
const GEO_CACHE_KEY = 'padelmesh.searchGeo'
const GEO_CACHE_TTL = 15 * 60 * 1000

// innodb_ft_min_token_size = 3: запрос короче трёх символов не попадёт ни в
// один токен индекса, отправлять его на сервер бессмысленно.
const MIN_QUERY_LENGTH = 3

const RECENT_STORAGE_KEY = 'padelmesh.recentSearches'
const RECENT_LIMIT = 5

/**
 * @file Единая строка поиска: наши сущности плюс точки с карты.
 *
 * Выдача в одном списке: сверху наша база (города, страны, клубы, тренеры,
 * сети, товары, статьи), ниже отдельной группой — Google Places.
 *
 * В Google идём только если своей локации не нашлось. На хабах товаров и статей Places
 * выключен целиком (`places: false`).
 *
 * Выбор точки с карты сабмитит форму с координатами — по ним страница
 * результатов показывает ближайшие клубы для города, которого у нас нет.
 */
export default class SearchFormController extends Controller {
  static targets = ['input', 'latitude', 'longitude']

  static values = {
    loadingLabel: String,
    suggestionsUrl: String,
    context: String,
    recentLabel: String,
    placesLabel: String,
    places: { type: Boolean, default: true }
  }

  #timer = null
  #abortController = null
  #requestId = 0
  #dropdown = null
  #entries = []
  #ourEntries = []
  #placeEntries = []
  // Нормализованные названия всех наших совпавших локаций — фильтр для Places.
  #ourLocationKeys = new Set()
  #heading = null
  #activeIndex = -1
  #onKeydown = null
  #onBlur = null
  // Точка пользователя для locationBias. Запрашивается только там, где строка
  // и правда про «куда поехать» (см. #geolocate).
  #locationBias = null
  #geoRequested = false

  connect() {
    if (!this.hasInputTarget) return

    this.#onKeydown = (event) => this.#handleKeydown(event)
    this.#onBlur = () => setTimeout(() => this.#close(), 150)
    this.inputTarget.addEventListener('keydown', this.#onKeydown)
    this.inputTarget.addEventListener('blur', this.#onBlur)

    // Maps грузится вне жизненного цикла Stimulus и может быть ещё не готов.
    // Ждать его нечем: готовность проверяется в момент запроса, а до тех пор
    // работает только наш источник — выпадашка от этого не ломается.
  }

  disconnect() {
    clearTimeout(this.#timer)
    this.#abort()

    if (this.hasInputTarget) {
      if (this.#onKeydown)
        this.inputTarget.removeEventListener('keydown', this.#onKeydown)
      if (this.#onBlur)
        this.inputTarget.removeEventListener('blur', this.#onBlur)
    }

    this.#dropdown?.remove()
  }

  changed(event) {
    // Ошибка «обязательное поле» показывается только по сабмиту — как только
    // человек редактирует поле, прячем её.
    this.#inputController(event.target)?.reset()

    // Правка строки руками отменяет координаты ранее выбранной точки: иначе
    // «Королёв» отправился бы с точкой от «Королёв, Бельгия».
    this.#clearCoordinates()

    this.#schedule(event.target.value.trim())
  }

  // Пустое поле в фокусе — показываем, куда человек уже ходил. Это единственная
  // осмысленная подсказка, пока он ничего не ввёл.
  focused(event) {
    if (event.target.value.trim() !== '') return

    this.#renderRecent()
  }

  /**
   * Спрашивает у браузера местоположение и превращает его в locationBias для
   * Google. Это мягкое предпочтение, а не фильтр: находится то же самое, просто
   * ближнее идёт выше — «San» в Испании даст San Sebastián, а не San Francisco.
   *
   * Вешается только там, где строка означает «куда поехать» (главная, страница
   * страны).
   */
  geolocate() {
    if (!this.placesValue) return
    if (!('geolocation' in navigator) || !window.isSecureContext) return

    const cached = this.#readGeoCache()
    if (cached) {
      this.#locationBias = cached
      return
    }

    if (this.#geoRequested) return
    this.#geoRequested = true

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude, accuracy } }) => {
        const bias = {
          center: { lat: latitude, lng: longitude },
          radius: accuracy
        }
        this.#locationBias = bias
        this.#writeGeoCache(bias)
        this.#geoRequested = false
      },
      () => {
        this.#geoRequested = false
      }
    )
  }

  // Чип популярной локации: подставляем название и сразу ищем, а не уходим на
  // страницу — выбор всё равно за человеком.
  selectChip(event) {
    const location = event.currentTarget.dataset.location
    if (!location || !this.hasInputTarget) return

    this.inputTarget.value = location
    this.inputTarget.focus()
    this.#schedule(location)
  }

  submit(event) {
    const field = this.element.querySelector('input[data-input-target="input"]')

    if (field && field.value.trim() === '') {
      event.preventDefault()
      this.#inputController(field)?.validate()
      field.focus()
      return
    }

    showPreloader({ label: this.loadingLabelValue })
  }

  #inputController(field) {
    const inputElement = field.closest('[data-controller~="input"]')
    if (!inputElement) return null

    return this.application.getControllerForElementAndIdentifier(
      inputElement,
      'input'
    )
  }

  #schedule(query) {
    clearTimeout(this.#timer)

    if (query.length < MIN_QUERY_LENGTH) {
      this.#abort()

      // Пустое поле — показываем недавние переходы; недобранная строка просто закрывает выпадашку.
      if (query === '') {
        this.#renderRecent()
      } else {
        this.#close()
      }

      return
    }

    this.#timer = setTimeout(() => this.#fetch(query), SUGGESTIONS_DEBOUNCE)
  }

  // Предыдущий запрос именно обрываем, а не просто игнорируем его ответ: при
  // поиске по семи сущностям брошенная работа стоит и серверу, и сети.
  // У Places отмены нет — там ответ отсекается по номеру запроса.
  #abort() {
    this.#abortController?.abort()
    this.#abortController = null
    this.#requestId++
  }

  async #fetch(query) {
    this.#abort()
    this.#abortController = new AbortController()
    const requestId = ++this.#requestId

    // Заголовок «Недавние» относится только к истории. Путь «фокус на пустом
    // поле → ввод трёх символов» не проходит через #close, поэтому без сброса
    // живая выдача рисовалась бы под чужим заголовком.
    this.#heading = null
    this.#ourEntries = await this.#requestOurs(query)
    if (requestId !== this.#requestId) return

    this.#placeEntries = []
    this.#render()

    // Google зовём только когда своей локации не нашлось. Из-за этого его
    // группа появляется на время нашего запроса позже, чем если бы источники
    // шли параллельно, — зато на «Amsterdam» и «Spain» платного запроса нет
    // вовсе. Наши подсказки в обоих случаях рисуются в один и тот же момент.
    if (!this.#shouldAskPlaces()) return

    const places = await this.#requestPlaces(query)
    if (requestId !== this.#requestId) return

    this.#placeEntries = this.#withoutOurLocations(places)
    this.#render()
  }

  // Проверяем по всем совпавшим локациям, а не по показанным: город мог не
  // попасть в лимит выдачи, но он всё равно наш, и точка с карты его дублирует.
  #shouldAskPlaces() {
    if (!this.placesValue || !placesAvailable()) return false

    return this.#ourLocationKeys.size === 0
  }

  async #requestOurs(query) {
    const url = new URL(this.suggestionsUrlValue, window.location.origin)
    url.searchParams.set('q', query)
    if (this.hasContextValue) url.searchParams.set('context', this.contextValue)

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!response.ok)
        throw new Error(`Suggestions failed: ${response.status}`)

      const data = await response.json()
      this.#ourLocationKeys = new Set(
        (Array.isArray(data.location_names) ? data.location_names : []).map(
          locationKey
        )
      )

      return Array.isArray(data.items) ? data.items : []
    } catch (error) {
      // AbortError — наша собственная отмена, а не сбой.
      if (error.name !== 'AbortError') this.#ourLocationKeys = new Set()

      return []
    }
  }

  async #requestPlaces(query) {
    try {
      const suggestions = await fetchPlaceSuggestions(query, {
        locationBias: this.#locationBias
      })

      return suggestions.map((suggestion) => this.#placeEntry(suggestion))
    } catch {
      return []
    }
  }

  // Первая часть строки Places — само название («Amsterdam» из «Amsterdam,
  // Noord-Holland, Netherlands»), остальное уходит во вторую строку карточки.
  #placeEntry({ text, placeId }) {
    const [name, ...rest] = text.split(',').map((part) => part.trim())

    return {
      type: 'place',
      icon: 'map-pin',
      name,
      subtitle: rest.join(', '),
      query: text,
      placeId
    }
  }

  // Локация, которая есть у нас, из Places выбрасывается. Сравниваем со ВСЕМИ
  // совпавшими названиями, а не только с показанными: город мог не попасть в
  // лимит выдачи, но он всё равно наш.
  #withoutOurLocations(entries) {
    const known = this.#ourLocationKeys || new Set()

    return entries.filter((entry) => !known.has(locationKey(entry.name)))
  }

  #renderRecent() {
    const recent = this.#readRecent()

    this.#ourEntries = recent
    this.#placeEntries = []
    this.#heading = recent.length > 0 ? this.recentLabelValue : null
    this.#render()
  }

  #render() {
    const dropdown = this.#ensureDropdown()
    const sections = []

    if (this.#ourEntries.length > 0)
      sections.push({ heading: this.#heading, entries: this.#ourEntries })
    if (this.#placeEntries.length > 0)
      sections.push({
        heading: this.placesLabelValue,
        entries: this.#placeEntries
      })

    this.#entries = sections.flatMap((section) => section.entries)
    if (this.#entries.length === 0) {
      this.#close()
      return
    }

    dropdown.innerHTML = ''
    this.#activeIndex = -1

    let index = 0
    sections.forEach((section) => {
      if (section.heading)
        dropdown.appendChild(this.#buildHeading(section.heading))
      section.entries.forEach((entry) => {
        dropdown.appendChild(this.#buildItem(entry, index))
        index += 1
      })
    })

    dropdown.style.display = 'block'
    this.inputTarget.setAttribute('aria-expanded', 'true')
  }

  #buildHeading(text) {
    const title = document.createElement('p')
    title.className = 'search-suggestions__heading'
    title.textContent = text

    return title
  }

  #buildItem(entry, index) {
    const item = document.createElement('a')
    item.className = 'search-suggestions__item'
    item.href = entry.url || '#'
    item.dataset.type = entry.type || ''
    item.setAttribute('role', 'option')
    item.setAttribute('aria-selected', 'false')

    item.appendChild(this.#buildVisual(entry))

    const body = document.createElement('span')
    body.className = 'search-suggestions__body'

    const name = document.createElement('span')
    name.className = 'search-suggestions__name'
    name.textContent = entry.name
    body.appendChild(name)

    if (entry.subtitle) {
      const subtitle = document.createElement('span')
      subtitle.className = 'search-suggestions__subtitle'
      subtitle.textContent = entry.subtitle
      body.appendChild(subtitle)
    }

    item.appendChild(body)

    // mousedown до blur: без preventDefault поле теряет фокус раньше клика, и
    // выпадашка успевает закрыться.
    item.addEventListener('mousedown', (event) => event.preventDefault())
    item.addEventListener('click', (event) => {
      event.preventDefault()
      this.#select(index)
    })

    return item
  }

  // Иконка типа слева — единственное, что отличает локацию от клуба и тренера.
  // Спрайт уже в разметке страницы (layouts/_icons_sprite), поэтому достаточно
  // ссылки на символ.
  #buildVisual(entry) {
    if (entry.thumbnail_url) {
      const image = document.createElement('img')
      image.className = 'search-suggestions__image'
      image.src = entry.thumbnail_url
      image.alt = ''
      image.width = 40
      image.height = 40
      return image
    }

    const wrapper = document.createElement('span')
    wrapper.className = 'search-suggestions__icon'
    wrapper.setAttribute('aria-hidden', 'true')
    wrapper.innerHTML = `<svg width="1em" height="1em" fill="none"><use href="#icon-${entry.icon || 'search'}"></use></svg>`

    return wrapper
  }

  #ensureDropdown() {
    if (this.#dropdown) return this.#dropdown

    const container =
      this.inputTarget.closest('.input') || this.inputTarget.parentElement
    if (getComputedStyle(container).position === 'static')
      container.style.position = 'relative'

    const dropdown = document.createElement('div')
    dropdown.className = 'search-suggestions'
    dropdown.setAttribute('role', 'listbox')
    dropdown.style.display = 'none'
    container.appendChild(dropdown)

    this.#dropdown = dropdown
    return dropdown
  }

  #close() {
    this.#entries = []
    this.#ourEntries = []
    this.#placeEntries = []
    this.#heading = null
    this.#activeIndex = -1

    if (this.#dropdown) {
      this.#dropdown.style.display = 'none'
      this.#dropdown.innerHTML = ''
    }
    if (this.hasInputTarget)
      this.inputTarget.setAttribute('aria-expanded', 'false')
  }

  #handleKeydown(event) {
    if (this.#entries.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.#moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.#moveActive(-1)
        break
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault()
          this.#select(this.#activeIndex)
        }
        break
      case 'Escape':
        this.#close()
        break
    }
  }

  #moveActive(delta) {
    const count = this.#entries.length
    this.#activeIndex = (this.#activeIndex + delta + count) % count

    const items =
      this.#dropdown?.querySelectorAll('.search-suggestions__item') || []
    items.forEach((item, index) => {
      const active = index === this.#activeIndex
      item.classList.toggle('search-suggestions__item--active', active)
      item.setAttribute('aria-selected', active ? 'true' : 'false')
    })
  }

  #select(index) {
    const entry = this.#entries[index]
    if (!entry) return

    clearTimeout(this.#timer)
    this.#abort()

    if (entry.type === 'place') {
      this.#submitPlace(entry)
      return
    }

    if (!entry.url) return

    this.#rememberRecent(entry)
    this.#close()
    Turbo.visit(entry.url)
  }

  /**
   * Точка с карты своей страницы не имеет — уходим на выдачу поиска с
   * координатами, по ним сервер показывает ближайшие клубы.
   *
   * Координаты дотягиваются до сабмита, а не после: страница результатов должна
   * получить их в том же запросе.
   */
  async #submitPlace(entry) {
    this.inputTarget.value = entry.query
    this.#close()
    showPreloader({ label: this.loadingLabelValue })

    try {
      const location = await fetchPlaceLocation(entry.placeId)
      if (location) this.#setCoordinates(location)
    } catch {
      // Без координат выдача просто не покажет «ближайшие клубы» — это лучше,
      // чем не отправить форму вовсе.
    }

    this.element.requestSubmit()
  }

  #setCoordinates({ latitude, longitude }) {
    if (!this.hasLatitudeTarget || !this.hasLongitudeTarget) return

    this.latitudeTarget.value = latitude
    this.longitudeTarget.value = longitude
  }

  #clearCoordinates() {
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ''
    if (this.hasLongitudeTarget) this.longitudeTarget.value = ''
  }

  #readGeoCache() {
    try {
      const cached = JSON.parse(getItem(GEO_CACHE_KEY))
      if (cached && Date.now() - cached.savedAt < GEO_CACHE_TTL)
        return cached.bias
    } catch {
      // Отказ от функциональных cookie и битый JSON — не повод ломать поиск.
    }

    return null
  }

  #writeGeoCache(bias) {
    setItem(GEO_CACHE_KEY, JSON.stringify({ bias, savedAt: Date.now() }))
  }

  // Недавние переходы держим в localStorage, а не на сервере: это личная
  // история, она нужна только в этом браузере и не стоит своей таблицы.
  //
  // Через getItem/setItem, а не напрямую: обёртка проверяет согласие на
  // функциональные cookie и без него молча ничего не пишет.
  #readRecent() {
    try {
      const stored = JSON.parse(getItem(RECENT_STORAGE_KEY) || '[]')

      return Array.isArray(stored) ? stored.slice(0, RECENT_LIMIT) : []
    } catch {
      return []
    }
  }

  #rememberRecent(entry) {
    try {
      const stored = this.#readRecent().filter((item) => item.url !== entry.url)
      stored.unshift({
        type: entry.type,
        icon: entry.icon,
        name: entry.name,
        subtitle: entry.subtitle,
        url: entry.url
      })

      setItem(RECENT_STORAGE_KEY, JSON.stringify(stored.slice(0, RECENT_LIMIT)))
    } catch {
      // Приватный режим и переполненное хранилище не должны ломать переход.
    }
  }
}
